import React, { useMemo, useState, VFC } from 'react';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import getStripe from 'src/common/stripe';
import { apiSubscribeMonthly, apiCreateBillingPortalSession } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { Nullable } from 'src/types/nullable.type';
import { withFixedLoadingSpinner } from 'src/common/components/loading-spinner/hooks/with-loading-spinner';
import { Subscription, SubscriptionPaymentIntervalTypes, SubscriptionPlanObject, SubscriptionTypes } from '.';
import { DashboardSettingsBillingInvoiceList, DashboardSettingsBillingInvoiceListProps } from './invoice-list';
import { DashboardSettingsBillingSubscriptionPlans } from './subscription/plans';
import { getWordWithCount } from 'src/utils/text';
import { formatUnixTimestampSecondsToDate } from 'src/utils/date';
import { DashboardSettingsBillingCurrentPlanFactory } from './current-plan.factory';

interface DashboardSettingsBillingSectionComponentProps extends DashboardSettingsBillingInvoiceListProps {
  subscription: Nullable<Subscription>;
  plans: Nullable<SubscriptionPlanObject>;
}

const DashboardSettingsBillingSectionComponent: VFC<DashboardSettingsBillingSectionComponentProps> = ({
  invoices,
  subscription,
  plans,
}) => {
  const { currentOrganization } = useCurrentOrganization();
  const [userSubscription] = useState(subscription);
  const { setErrorNotification } = useNotificationMessage();
  const userIsSubscribed = useMemo(() => userSubscription?.status === 'active', [userSubscription]);
  const userIsOnTrial = useMemo(() => userSubscription?.status === 'trialing', [userSubscription]);

  const subscriptionPlanType = useMemo(
    () => userSubscription?.plan_type,
    [userSubscription]
  );
  const subsWillBeCanceledAtPeriod = useMemo(
    () => userSubscription?.cancel_at_period_end && userSubscription?.cancel_at,
    [userSubscription]
  );
  const isCanceledAtPeriodEnd = useMemo(
    () => (userIsOnTrial || userIsSubscribed) && subsWillBeCanceledAtPeriod,
    [subsWillBeCanceledAtPeriod, userIsOnTrial, userIsSubscribed]
  ) as boolean;

  const billableUserCountText = getWordWithCount(
    'user',
    'users',
    userIsSubscribed ? userSubscription?.quantity : currentOrganization.num_of_billable_users
  );

  const renewsOnText = `Renews on ${formatUnixTimestampSecondsToDate(userSubscription?.current_period_end)}`;

  const CurrentPlan = DashboardSettingsBillingCurrentPlanFactory(userSubscription, {
    isSubscribed: userIsSubscribed,
    isOnTrial: userIsOnTrial,
    isCanceledAtPeriodEnd,
    billableUserCountText,
    renewsOnText,
  });

  const onUpgradeClick = (subscriptionType: SubscriptionTypes,intervalType: SubscriptionPaymentIntervalTypes) => {
    apiSubscribeMonthly(currentOrganization.id, { method: intervalType, plan: subscriptionType }).then((response) => {
      const stripe = getStripe();
      if (stripe) {
        stripe.redirectToCheckout({ sessionId: response.session_id });
      } else {
        setErrorNotification('Stripe is not available. Please refresh the page and try again.');
      }
    });
  };

  // Cancellation goes through Stripe's own billing portal rather than a
  // third-party deflection flow. The previous flow handed control to Raaft and
  // never checked the outcome: if Raaft's survey did not complete, nothing was
  // cancelled and no error was shown, so customers were left still paying with
  // no way through. Stripe also covers plan changes, payment methods and
  // invoices, none of which we then maintain ourselves.
  const onDowngradeToFreeClicked = () => {
    apiCreateBillingPortalSession(currentOrganization.id)
      .then((response: { portal_url?: string }) => {
        if (!response?.portal_url) {
          throw new Error('No portal URL returned');
        }
        window.location.href = response.portal_url;
      })
      .catch(() => {
        setErrorNotification("Couldn't open billing settings. Please try again.");
      });
  };

  return (
    <div className='flex flex-col space-y-16'>
      {invoices.length !== 0 && (
        <div className='flex flex-col space-y-4'>
          <span className='text-bedrock-p-strong text-bedrock-black'>Invoices</span>
          <DashboardSettingsBillingInvoiceList invoices={invoices} />
        </div>
      )}
      <DashboardSettingsBillingSubscriptionPlans
        paymentInterval={subscription?.plan.interval}
        isQuarterly={subscription?.plan.interval === 'month' && subscription?.plan.transform_usage !== null}
        plans={plans || {'starter':[],'pro':[],'enterprise':[]} }
        renderCurrentPlan={() => (
          <div className='flex'>
            <CurrentPlan />
          </div>
        )}
        subscriptionPlanType={subscriptionPlanType?subscriptionPlanType:null}
        isSubscribed={userIsSubscribed}
        isPaused={subscription?.pause_collection !== null}
        isOnTrial={userIsOnTrial}
        isCanceledAtPeriodEnd={isCanceledAtPeriodEnd}
        onUpgradeClick={onUpgradeClick}
        onDowngradeToFreeClick={onDowngradeToFreeClicked}
      />
    </div>
  );
};

export const DashboardSettingsBillingSection = withFixedLoadingSpinner<DashboardSettingsBillingSectionComponentProps>(
  DashboardSettingsBillingSectionComponent
);
